import { isMatch } from "../pattern/isMatch";
import { PhonemeInfo } from "../pattern/phonemeInfo";
import { ipaToFeatures, IpaType, isStress, isTone, syllableBreak } from "../phonemes/ipa";
import { getSonorityLevel } from "./orderBySonority";
import { SyllablePhonotactics } from "./phonotactics";

/**
 * figure out the syllables in the word using the possible syllable building blocks,
 * @param phonotactics either phonotactics or a list of diphthongs that can be nuclei
 * @param useTones if tone symbols are present, assume they break up the syllables
 * @returns an array of lengths of the syllables, or false if it didn't match
 */
export function findSyllables(
  input: IpaType[],
  phonotactics: SyllablePhonotactics<PhonemeInfo> | IpaType[],
  useTones?: 'tones'
): number[] | false {
  if (input.some(p => p === syllableBreak)) {
    return bySyllableBreaks(input);
  }
  if (useTones === 'tones' && input.some(p => isTone(p))) {
    return byTones(input);
  }
  if (Array.isArray(phonotactics)) {
    const syllables = genericSyllables(input, phonotactics);
    return fixupSyllables(input, syllables);
  }
  const finder = new SyllableFinder(input, phonotactics);
  return finder.findSyllables();
}

/** break into syllables assuming explicit syllable breaks are correct */
function bySyllableBreaks(input: IpaType[]): number[] {
  const syllables: number[] = [];
  let count = 0;
  for (const p of input) {
    count++;
    if (p === syllableBreak) {
      syllables.push(count);
      count = 0;
    }
  }
  if (count > 0) {
    syllables.push(count);
  }
  return syllables;
}

/** break into syllables assuming tones come at the end of a syllable */
function byTones(input: IpaType[]): number[] {
  const syllables: number[] = [];
  let count = 0;
  let lastWasTone = false;
  for (const p of input) {
    const thisIsTone = isTone(p);
    if (lastWasTone && !thisIsTone) {
      syllables.push(count);
      count = 1;
    } else {
      count++;
    }
    lastWasTone = thisIsTone;
  }
  if (count > 0) {
    syllables.push(count);
  }
  return syllables;
}

/** returns the onset/nucleus/coda of a single syllable */
export function findSyllablePieces(
  input: IpaType[], phonotactics: SyllablePhonotactics<PhonemeInfo>
): false | { onset: IpaType[], nucleus: IpaType[], coda: IpaType[] } {
  const finder = new SyllableFinder(input, phonotactics);
  return finder.getFirstPieces();
}

class SyllableFinder {
  readonly syllables: number[] = [];

  private current: number = 0;
  private onsetLength: number = 0;
  private nucleusLength: number = 0;

  constructor(
    private readonly input: IpaType[],
    private readonly phonotactics: SyllablePhonotactics<PhonemeInfo>
  ) {
    // prep for findSyllables() by finding first onset & nucleus
    const check = this.getOnsetAndNucleus();
    if (check !== false) {
      this.onsetLength = check[0];
      this.nucleusLength = check[1];
      this.current = check[0] + check[1];
    }
  }

  /**
   * find all the syllables, returning each of their lengths,
   * or false if the word didn't match the patterns in the given phonotactics
   */
  findSyllables(): number[] | false {
    let result;
    do {
      result = this.findNextPiece();
    } while (result === 'more');
    return result === 'fail' ? false : this.syllables;
  }

  /** if called right after the ctor, this returns the onset/nucleus/coda of the first syllable */
  getFirstPieces(): false | { onset: IpaType[], nucleus: IpaType[], coda: IpaType[] } {
    const codaLength = isMatch(this.phonotactics.coda, this.input, this.current);
    if (codaLength === false) {
      return false;
    }
    const codaStart = this.onsetLength + this.nucleusLength;
    const onset = this.input.slice(0, this.onsetLength);
    const nucleus = this.input.slice(this.onsetLength, codaStart);
    const coda = this.input.slice(codaStart, codaStart + codaLength.count);
    return { onset, nucleus, coda };
  }

  /**
   * pre: (onset1,nucleus1,), read (,,coda1),
   * if more word left, read (onset2,nucleus2,)
   * and adjust what's in (,,coda1),(onset2,,) to maximize onset2
   */
  private findNextPiece(): 'more' | 'fail' | 'done' {
    // get (,,coda1)
    const codaLength = isMatch(this.phonotactics.coda, this.input, this.current);
    if (codaLength === false) {
      return 'fail';  // couldn't match entire syllable
    }
    this.current += codaLength.count;
    if (this.current >= this.input.length) {
      this.syllables.push(this.onsetLength + this.nucleusLength + codaLength.count);
      return 'done';  // we've matched entire word
    }

    // get (onset2,nucleus2,)
    const test = this.getOnsetAndNucleus();
    if (test === false) {
      return 'fail';
    }
    const [onsetLength, nucleusLength] = test;

    // possibly adjust the dividing line between coda1 & onset2
    this.current -= codaLength.count;
    const [coda1, onset2] = this.shiftDivider(codaLength.count, onsetLength);
    const lastLength = this.onsetLength + this.nucleusLength + coda1;
    this.syllables.push(lastLength);
    this.onsetLength = onset2;
    this.nucleusLength = nucleusLength;
    this.current += coda1 + onset2 + nucleusLength;

    return 'more';
  }

  private getOnsetAndNucleus(): [number, number] | false {
    const onsetLength = isMatch(this.phonotactics.onset, this.input, this.current, isStress);
    if (onsetLength === false) {
      return false;
    }
    const nucleusLength = isMatch(this.phonotactics.nucleus, this.input, this.current + onsetLength.count, isStress);
    if (nucleusLength !== false) {
      return [onsetLength.count, nucleusLength.count];
    }
    // failed

    if (onsetLength.count > 0) {
      // usually it's ok to gobble up as much as possible for the 'onset',
      // but now we'll try shortening the onset & trying again,
      // useful when the same phoneme can be in the 'onset' or 'nucleus', e.g. /n/
      const onset2 = isMatch(this.phonotactics.onset, this.input.slice(this.current, onsetLength.count - 1), 0, isStress);
      if (onset2 !== false) {
        const nucleus2 = isMatch(this.phonotactics.nucleus, this.input, this.current + onset2.count, isStress);
        if (nucleus2 !== false) {
          return [onset2.count, nucleus2.count];
        }
      }
    }
    return false;
  }

  /**
   * move as much of coda1 to onset2 as possible
   */
  private shiftDivider(coda1Length: number, onset2Length: number): [number, number] {
    if (coda1Length === 0) {
      return [coda1Length, onset2Length];
    }
    for (let c = 0, o = coda1Length + onset2Length; c < coda1Length; c++, o--) {
      // trim off end so that it has to match a shorter coda
      const chopped = this.input.slice(0, this.current + c);
      const newCoda = isMatch(this.phonotactics.coda, chopped, this.current);
      if (newCoda !== false) {
        const newOnset = isMatch(this.phonotactics.onset, this.input, this.current + newCoda.count, isStress);
        if (newOnset !== false) {
          const newNucleus = isMatch(this.phonotactics.nucleus, this.input, this.current + newCoda.count + newOnset.count, isStress);
          if (newNucleus !== false) {
            // we managed to match coda/onset/nucleus, so this is the best valid match
            return [newCoda.count, newOnset.count];
          }
        }
      }
    }
    // couldn't find anything better
    return [coda1Length, onset2Length];
  }
}


/**
 * assume nucleus is (single vowel | syllabic consonant | diphthong), coda & onset should
 * be split based on sonority hierarchy, prefer having a longer onset & shorter coda
 */
function genericSyllables(input: IpaType[], diphthongs: IpaType[]): number[] {
  const syllables: number[] = [];
  let i = 0;
  while (i < input.length) {
    const next = findNextNucleus(input, i, diphthongs);
    if (next === false) {
      if (syllables.length > 0) {
        // add final coda onto last syllable
        syllables[syllables.length - 1] += input.length - i;
      } else {
        // just a single syllable with no nucleus
        syllables.push(input.length);
      }
      break;
    } else {
      const [start, length] = next;
      syllables.push(length + start - i);
      i = start + length;
    }
  }
  return syllables;
}

/** return [start, length] of next nucleus after i, or false if none */
function findNextNucleus(input: IpaType[], i: number, diphthongs: IpaType[]): [number, number] | false {
  for (let j = i; j < input.length; j++) {
    // check for diphthong
    const diphthong = diphthongs.find(d => isDiphthong(input, j, d));
    if (diphthong || isVowelPair(input, j)) {
      const codaLength = codaBySonority(input, j + 2);
      return [j, 2 + codaLength];
    }

    // check for vowel or syllabic consonant
    const features = ipaToFeatures(input[j]);
    if (features && (
      (features.includes('vowel') && !features.includes('non-syllabic'))
      || features.includes('syllabic'))
    ) {
      const codaLength = codaBySonority(input, j + 1);
      const toneCount = checkForTones(input, j + codaLength + 1);
      return [j, 1 + codaLength + toneCount];
    }
  }
  return false;
}

/** true if input[i] is the start of this diphthong */
function isDiphthong(input: IpaType[], i: number, diphthong: IpaType): boolean {
  let j = 0;
  for (const c of diphthong) {
    if (c !== input[i + j]) {
      return false;
    }
    j++;
  }
  return true;
}

/** true if input[i] is a vowel & input[i+1] is non-syllabic vowel */
function isVowelPair(input: IpaType[], i: number): boolean {
  const features1 = ipaToFeatures(input[i]);
  const features2 = ipaToFeatures(input[i + 1]);
  return features1 !== undefined && features2 !== undefined
    && features1.includes('vowel')
    && features2.includes('vowel') && features2.includes('non-syllabic');
}

/**
 * assuming input[start] is immediately after a nucleus,
 * this returns the number of phonemes in the coda based on sonority.
 */
function codaBySonority(input: IpaType[], start: number): number {
  const sonority = getSonorityLevel(input[start - 1]);
  if (sonority === false) {
    return 0;
  }
  // keep moving forward till we either hit a vowel or increase sonority,
  // the coda shouldn't include either of those
  let lastLevel = sonority[0];
  for (let i = start; i < input.length; i++) {
    const nextSonority = getSonorityLevel(input[i]);
    if (nextSonority !== false && nextSonority[1] !== 'X') { // ignore clicks
      const [level, syllableCat] = nextSonority;
      if (syllableCat === 'V' || level > lastLevel) {
        // coda length doesn't include last phoneme
        return i === start ? 0 : i - start - 1;
      }
      lastLevel = nextSonority[0];
    }
  }
  return 0;
}

/** return the number of tone symbols starting from input[start] */
function checkForTones(input: IpaType[], start: number): number {
  let i = start;
  while (i < input.length && isTone(input[i])) {
    i++;
  }
  return i - start;
}

/** if there are combiner symbols, try to honor them when aligning syllables */
function fixupSyllables(input: IpaType[], syllables: number[]): number[] {
  if (!input.some(p => isCombiner(p))) {
    return syllables;
  }

  const result = syllables.map(n => n);
  for (let i = 1, current = syllables[0]; i < syllables.length; i++, current += syllables[i]) {
    if (isCombiner(input[current])) {
      // syllable starts with combiner
      const features1 = ipaToFeatures(input[current - 1]);
      const features2 = ipaToFeatures(input[current + 1]);
      if (features1?.includes('vowel') && features2?.includes('vowel')) {
        // if V-V, merge previous syllable into this one
        result[i] += result[i - 1];
        result[i - 1] = 0;
      } else {
        // otherwise, move the previous symbol into the previous syllable
        result[i - 1]--;
        result[i]++;
      }
    } else if (isCombiner(input[current - 1])) {
      // last syllable ends with combiner,
      // move start of current syllable into last one
      result[i - 1]++;
      result[i]--;
    }
  }
  // get rid of any syllables that were reduced to 0
  return result.filter(n => n !== 0);
}

function isCombiner(s: IpaType): boolean {
  const features = ipaToFeatures(s);
  return features !== undefined && (features.includes('no-break') || features.includes('combine'));
}
